import { numberFormatter } from '@components/AgGrid/utils';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import type { ConstructionTypeData, UercCalculationSeasonData } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { formatNumberToRu } from 'utils';

import type { ColDef } from 'ag-grid-community';
import type { ColGroupDef } from 'ag-grid-community/dist/types/core/entities/colDef';
import { useCallback, useMemo } from 'react';

import { bimcCalculationLimitedCostNameFilterGql } from '../bimCalculationLimitedQueries';
import { cellClass, BimMenuCellRenderer, notSubHeaderRow } from '../components/BimMenuCellRenderer';

interface IUseBimCalculationLimitedCostsColumns {
    seasons?: UercCalculationSeasonData[] | null;
    costRounding: number;
    constructionTypes: ConstructionTypeData[];
    calculationId?: number;
    constructionTypeId: number;
    onOperationSuccess?: (id?: number) => void;
    changeFilterHandler: (params: { fieldName: string; checkedValues: string[] }) => void;
    isCalculationCompleted: boolean;
}

export const useBimCalculationLimitedCostsColumns = (
    props: IUseBimCalculationLimitedCostsColumns,
): {
    fullSetSettingsColumns: ColDef[];
    tableSettingsConfirm: (visible: unknown) => void;
    columns: (ColDef<any> | ColGroupDef<any>)[];
    isColumnVisible: (id: string) => boolean;
} => {
    const {
        seasons,
        costRounding,
        constructionTypes,
        calculationId,
        constructionTypeId,
        onOperationSuccess,
        changeFilterHandler,
        isCalculationCompleted,
    } = props;

    const filterVariables = useMemo(
        () => ({
            calculationId,
            constructionTypeId,
        }),
        [constructionTypeId, calculationId],
    );

    const seasonsColumns = useMemo<ColDef[]>(
        () =>
            (seasons ?? []).map((season: UercCalculationSeasonData) => ({
                headerName: `Сезон ${season.year}`,
                flex: 1,
                minWidth: 160,
                valueGetter: ({ data }) =>
                    notSubHeaderRow(data) ? formatNumberToRu(data[`season${season.seasonId}`], costRounding) : '',
                cellClass,
            })),
        [costRounding, seasons],
    );

    const columns = useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                headerName: '',
                field: 'menu',
                lockPosition: 'left',
                pinned: 'left',
                cellRenderer: BimMenuCellRenderer,
                cellRendererParams: {
                    constructionTypes,
                    calculationId,
                    constructionTypeId,
                    onOperationSuccess,
                    costRounding,
                    isCalculationCompleted,
                    seasons,
                },
                width: 75,
                cellClass,
                resizable: false,
            },
            {
                headerName: 'Наименование',
                field: 'name',
                minWidth: 448,
                flex: 1,
                cellClass,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: true,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'bimcCalculationLimitedCostNameFilter',
                    query: bimcCalculationLimitedCostNameFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: '',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Код',
                field: 'key',
                cellClass,
                minWidth: 160,
                flex: 1,
            },
            {
                headerName: '%',
                field: 'calculatingValue',
                valueFormatter: numberFormatter(3),
                minWidth: 114,
                flex: 1,
                cellClass,
            },
            {
                headerName: 'Всего',
                field: 'totalCost',
                valueFormatter: numberFormatter(costRounding),
                minWidth: 114,
                flex: 1,
                cellClass,
            },
            ...seasonsColumns,
            {
                headerName: 'Обоснование',
                field: 'rationale',
                minWidth: 140,
                flex: 1,
                cellClass,
            },
        ],
        [
            constructionTypes,
            calculationId,
            constructionTypeId,
            onOperationSuccess,
            costRounding,
            isCalculationCompleted,
            seasons,
            filterVariables,
            changeFilterHandler,
            seasonsColumns,
        ],
    );

    const [selection, , , setSelection] = useSelection('bimCalculationLimitedCosts');
    const [pinnedItems, , , setPinned] = usePin('bimCalculationLimitedCosts');

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };
                    const columnId = getColumnId(column);

                    if (idx < 2) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[columnId]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setSelection, setPinned],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
